import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import api from "@/lib/api";

function IconFor({ name, className }) {
  const I = Icons[name] || Icons.Flame;
  return <I className={className} />;
}

export default function MinistryDetail() {
  const { slug } = useParams();
  const [m, setM] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setM(null);
    setNotFound(false);
    api.get(`/ministries/${slug}`)
      .then((r) => setM(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-zinc-400 px-6 text-center">
        <p className="font-display text-3xl uppercase tracking-tight mb-3">Ministry not found</p>
        <Link to="/ministries" className="font-mono-accent text-xs uppercase tracking-widest text-[#ff5c00] hover:underline">
          ← Back to all ministries
        </Link>
      </div>
    );
  }
  if (!m) {
    return <div className="min-h-[50vh] flex items-center justify-center text-zinc-500 font-mono-accent text-xs tracking-widest uppercase">Loading…</div>;
  }

  return (
    <div data-testid="ministry-detail-page" className="bg-[#050505] text-white">
      {/* HERO */}
      <section className="relative min-h-[60vh] flex items-end overflow-hidden border-b border-white/10">
        <div className="absolute inset-0">
          {m.hero_image && (
            <img
              src={m.hero_image}
              alt={m.name}
              className="w-full h-full object-cover opacity-60"
              style={{
                objectPosition: m.hero_image_position || "center",
                transform: `scale(${m.hero_image_scale || 1})`,
                transformOrigin: m.hero_image_position || "center",
              }}
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-20 w-full">
          <Link to="/ministries" className="inline-flex items-center gap-1 font-mono-accent text-xs uppercase tracking-widest text-zinc-300 hover:text-[#ff5c00] mb-8">
            <Icons.ChevronLeft className="w-3 h-3" /> All ministries
          </Link>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center gap-3 mb-4">
              <IconFor name={m.icon} className="w-6 h-6 text-[#ff5c00]" />
              <span className="font-mono-accent text-xs tracking-[0.3em] uppercase text-[#ff5c00]">Ministry</span>
            </div>
            <h1 data-testid="ministry-title" className="font-display text-5xl md:text-7xl lg:text-8xl uppercase font-bold tracking-tighter leading-[0.9]">{m.name}</h1>
            <p className="text-zinc-300 text-lg md:text-xl max-w-3xl mt-6">{m.summary}</p>
            {m.meeting_time && (
              <div className="mt-6 inline-flex items-center gap-2 bg-[#ff5c00]/10 border border-[#ff5c00]/30 text-[#ff5c00] px-4 py-2 rounded-sm">
                <Icons.Clock className="w-4 h-4" />
                <span className="font-mono-accent text-xs uppercase tracking-widest">{m.meeting_time}</span>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* MISSION + LEADER */}
      <section className="py-20 px-6 md:px-12">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            {m.mission && (
              <>
                <p className="font-mono-accent text-xs tracking-[0.3em] uppercase text-[#ff5c00] mb-3">Our Mission</p>
                <p className="font-display text-2xl md:text-4xl uppercase tracking-tight leading-tight">
                  {m.mission}
                </p>
              </>
            )}
          </div>
          {(m.leader_name || m.leader_image) && (
            <div className="lg:col-span-5 bg-[#0f0f14] border border-white/10 rounded-sm p-6 flex items-center gap-5">
              {m.leader_image ? (
                <div className="w-20 h-20 rounded-sm overflow-hidden flex-shrink-0">
                  <img
                    src={m.leader_image}
                    alt={m.leader_name}
                    className="w-full h-full object-cover"
                    style={{
                      objectPosition: m.leader_image_position || "center",
                      transform: `scale(${m.leader_image_scale || 1})`,
                      transformOrigin: m.leader_image_position || "center",
                    }}
                  />
                </div>
              ) : (
                <div className="w-20 h-20 rounded-sm bg-[#ff5c00]/15 flex items-center justify-center">
                  <Icons.User className="w-8 h-8 text-[#ff5c00]" />
                </div>
              )}
              <div>
                <p className="font-mono-accent text-[10px] tracking-widest uppercase text-zinc-500">Led by</p>
                <p className="font-display text-2xl uppercase tracking-tight">{m.leader_name}</p>
                {m.leader_title && <p className="text-sm text-zinc-400">{m.leader_title}</p>}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* SECTIONS */}
      {(m.sections || []).length > 0 && (
        <section className="px-6 md:px-12 pb-12">
          <div className="max-w-7xl mx-auto space-y-10">
            {m.sections.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="grid lg:grid-cols-12 gap-8 border-t border-white/5 pt-10"
              >
                <div className={`lg:col-span-${s.image_url ? "7" : "12"} space-y-3`}>
                  {s.heading && <h2 className="font-display text-2xl md:text-4xl uppercase tracking-tight">{s.heading}</h2>}
                  {s.body && <p className="text-zinc-300 leading-relaxed whitespace-pre-wrap">{s.body}</p>}
                </div>
                {s.image_url && (
                  <div className="lg:col-span-5">
                    <div className="w-full h-72 rounded-sm overflow-hidden">
                      <img
                        src={s.image_url}
                        alt={s.heading || ""}
                        className="w-full h-full object-cover"
                        style={{
                          objectPosition: s.image_position || "center",
                          transform: `scale(${s.image_scale || 1})`,
                          transformOrigin: s.image_position || "center",
                        }}
                      />
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* GALLERY */}
      {(m.gallery_images || []).length > 0 && (
        <section className="py-20 px-6 md:px-12 bg-[#080808] border-y border-white/5">
          <div className="max-w-7xl mx-auto">
            <p className="font-mono-accent text-xs tracking-[0.3em] uppercase text-[#ff5c00] mb-3">In Action</p>
            <h2 className="font-display text-3xl md:text-5xl uppercase tracking-tight mb-8">Moments.</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {m.gallery_images.map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt=""
                  data-testid={`ministry-gallery-${i}`}
                  className="w-full h-44 object-cover rounded-sm hover:opacity-80 transition-opacity"
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-24 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center space-y-6">
          <h2 className="font-display text-3xl md:text-5xl uppercase tracking-tight">Want in?</h2>
          <p className="text-zinc-300 max-w-2xl mx-auto">
            We'd love to have you serve with us. Send us a note and we'll connect
            you with the {m.name} team.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/contact"
              data-testid="ministry-cta-join"
              className="inline-flex items-center gap-2 bg-[#ff5c00] hover:bg-[#ff7a33] text-white font-mono-accent text-xs tracking-[0.18em] uppercase px-7 py-4 rounded-sm transition-all hover:-translate-y-1"
            >
              {m.cta_text || "Join This Team"} <Icons.ArrowRight className="w-4 h-4" />
            </Link>
            {m.contact_email && (
              <a
                href={`mailto:${m.contact_email}?subject=Joining ${encodeURIComponent(m.name)}`}
                className="inline-flex items-center gap-2 border border-white/15 hover:border-[#ff5c00] hover:text-[#ff5c00] text-zinc-300 font-mono-accent text-xs uppercase tracking-widest px-6 py-4 rounded-sm"
              >
                <Icons.Mail className="w-4 h-4" /> Email the team
              </a>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
